import { useRef, useState } from "react";
import { Video as VideoIcon, Loader2, AlertCircle } from "lucide-react";

// Renders a video as a paused, non-interactive gallery thumbnail when the
// backend hasn't provided a thumbnail/poster (thumbnail_key is never set
// today, so this is the common case for every video asset).
//
// `preload="metadata"` alone doesn't guarantee a browser paints a frame —
// it just guarantees duration/dimensions are known. We explicitly seek to
// a tiny offset once metadata is available so the browser decodes and
// displays a real frame instead of leaving the canvas blank.
function VideoFirstFrameThumbnail({ src, assetId }) {
  const seekedRef = useRef(false); // guards against repeated seeks/loops
  const [failed, setFailed] = useState(false);

  const attemptSeek = (video) => {
    if (seekedRef.current || !video) return;
    if (!(video.duration > 0)) return; // duration not known yet — wait for a later event

    seekedRef.current = true;
    try {
      video.currentTime = Math.min(0.1, video.duration / 2);
    } catch {
      // Some browsers can throw if seeking is attempted at the wrong
      // readyState; the loadeddata handler below gives it another chance.
    }
  };

  const handleLoadedMetadata = (e) => attemptSeek(e.currentTarget);
  const handleLoadedData = (e) => attemptSeek(e.currentTarget);

  const handleError = () => {
    console.error("Media Library: video thumbnail failed to load", { assetId, src });
    setFailed(true);
  };

  if (failed) {
    return (
      <div className="flex h-full w-full items-center justify-center text-gray-300">
        <AlertCircle size={20} />
      </div>
    );
  }

  return (
    <video
      src={src}
      muted
      playsInline
      preload="metadata"
      tabIndex={-1}
      className="h-full w-full object-cover pointer-events-none"
      onLoadedMetadata={handleLoadedMetadata}
      onLoadedData={handleLoadedData}
      onError={handleError}
    />
  );
}

// One square cell in the media library grid. Purely presentational —
// branches on media_type/status to decide what to render.
export default function MediaAssetGridItem({ asset }) {
  const isVideo = asset.media_type === "video";

  // Non-ready assets (pending/processing/error) never get a real URL from
  // the backend — show a lightweight placeholder instead of a broken image.
  if (asset.status !== "ready") {
    return (
      <div className="relative aspect-square w-full overflow-hidden rounded bg-gray-100 flex flex-col items-center justify-center gap-1 text-gray-400">
        {asset.status === "error" ? (
          <>
            <AlertCircle size={20} />
            <span className="text-[10px]">Failed</span>
          </>
        ) : (
          <>
            <Loader2 size={20} className="animate-spin" />
            <span className="text-[10px] capitalize">{asset.status}</span>
          </>
        )}
      </div>
    );
  }

  // Prefer a real backend thumbnail (always the case for images; only ever
  // present for videos once backend thumbnail generation exists). Otherwise
  // fall back to the video itself, paused on its first frame.
  const thumbnail = asset.thumbnail_url || (!isVideo ? asset.url : null);

  return (
    <div className="relative aspect-square w-full overflow-hidden rounded bg-gray-100">
      {thumbnail ? (
        <img src={thumbnail} alt="" loading="lazy" className="h-full w-full object-cover" />
      ) : isVideo && asset.url ? (
        <VideoFirstFrameThumbnail src={asset.url} assetId={asset.id} />
      ) : (
        <div className="flex h-full w-full items-center justify-center text-gray-300">
          <AlertCircle size={20} />
        </div>
      )}

      {isVideo && (
        <span className="absolute bottom-1 right-1 flex items-center justify-center rounded-full bg-black/60 p-1">
          <VideoIcon size={12} className="text-white" />
        </span>
      )}
    </div>
  );
}
